import { FC, Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Add } from '@mui/icons-material'
import { Box, Button, Chip, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material'

import { useGetInternalMarketsQuery } from '../../../../api/internal-markets'
import { useGetInternalSettingQuery, useSaveInternalSettingMutation } from '../../../../api/settings'
import { DiscardChangesDialog } from '../../../../components/DiscardChangesDialog/DiscardChangesDialog'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { useEditPageLeave } from '../../../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { PerformanceCondition, PerformanceRuleKind, performanceRuleKinds, PerformanceSettingData } from '../../types/types'
import { PerformanceConditionGroup } from '../PerformanceConditionGroup/PerformanceConditionGroup'
import {
  addCondition,
  addConditionGroup,
  defaultPerformanceMarketIso,
  getConditionGroups,
  isPerformanceDataValid,
  isSamePerformanceData,
  removeCondition,
  toPerformanceSettingData,
  updateCondition,
} from '../helpers/performanceHelpers'

const emptyPerformanceData: PerformanceSettingData = {}
export const PerformanceRulesTab: FC = () => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const marketsQuery = useGetInternalMarketsQuery()
  const settingQuery = useGetInternalSettingQuery('performance')
  const [saveSetting, { isLoading: isSaving }] = useSaveInternalSettingMutation()
  const [draft, setDraft] = useState<PerformanceSettingData>()

  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [marketsQuery.data])
  const requestedMarketIso = searchParams.get('market') ?? defaultPerformanceMarketIso
  const marketIso = markets.some((market) => market.iso === requestedMarketIso) ? requestedMarketIso : markets[0]?.iso ?? requestedMarketIso

  const stored = (settingQuery.data?.data as PerformanceSettingData | undefined) ?? emptyPerformanceData
  const current = draft ?? stored
  const isDirty = !!draft && !isSamePerformanceData(draft, stored)
  const isValid = isPerformanceDataValid(current)
  // The settings tabs are separate routes, so this also asks before another tab replaces this one.
  const { guard } = useEditPageLeave(isDirty)

  const changeMarket = (iso: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('market', iso)
    setSearchParams(next, { replace: true })
  }

  const edit = (update: (data: PerformanceSettingData) => PerformanceSettingData) => setDraft(update(current))

  const handleSave = async () => {
    const data = toPerformanceSettingData(current)
    const outcome = await run(saveSetting({ name: 'performance', data, publicSetting: true }).unwrap(), {
      error: t('internal-service-management:settings_performance_save_failed'),
    })
    if (!outcome.ok) return
    const saved = outcome.result
    if (!saved || !isSamePerformanceData(data, (saved.data as PerformanceSettingData) ?? {})) {
      notifyError(t('internal-service-management:settings_performance_not_stored'))
      return
    }
    setDraft(undefined)
    notifySuccess(t('internal-service-management:settings_performance_saved'))
  }

  const renderRules = (kind: PerformanceRuleKind) => {
    const groups = getConditionGroups(current, marketIso, kind)
    return (
      <Stack spacing={1.5} component="section" aria-label={t(`internal-service-management:settings_performance_${kind}`)}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {t(`internal-service-management:settings_performance_${kind}`)}
          </Typography>
          <Typography color="text.secondary">{t(`internal-service-management:settings_performance_${kind}_description`)}</Typography>
        </Box>
        {groups.length === 0 && <Typography color="text.secondary">{t('internal-service-management:settings_performance_no_rules')}</Typography>}
        {groups.map((conditions, groupIndex) => {
          const path = { marketIso, kind, groupIndex }
          return (
            <Fragment key={groupIndex}>
              {groupIndex > 0 && (
                <Divider>
                  <Chip size="small" label={t('internal-service-management:settings_performance_or')} />
                </Divider>
              )}
              <PerformanceConditionGroup
                conditions={conditions}
                label={t('internal-service-management:settings_performance_group', { number: groupIndex + 1 })}
                onChange={(conditionIndex: number, change: Partial<PerformanceCondition>) =>
                  edit((data) => updateCondition(data, path, conditionIndex, change))
                }
                onRemove={(conditionIndex: number) => edit((data) => removeCondition(data, path, conditionIndex))}
                onAdd={() => edit((data) => addCondition(data, path))}
              />
            </Fragment>
          )
        })}
        <Box>
          <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => edit((data) => addConditionGroup(data, marketIso, kind))}>
            {t('internal-service-management:settings_performance_add_or')}
          </Button>
        </Box>
      </Stack>
    )
  }

  return (
    <QueryStateBoundary
      isLoading={marketsQuery.isLoading || settingQuery.isLoading}
      isError={marketsQuery.isError || settingQuery.isError}
      errorText={t('internal-service-management:settings_load_failed')}
    >
      <Stack spacing={3}>
        <Typography color="text.secondary">{t('internal-service-management:settings_performance_description')}</Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label={t('internal-service-management:settings_performance_market')}
            value={marketIso}
            onChange={(event) => changeMarket(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            {markets.map((market) => (
              <MenuItem key={market.iso} value={market.iso}>
                {market.name}
              </MenuItem>
            ))}
          </TextField>
          <Box flexGrow={1} />
          {isDirty && <Chip color="warning" variant="outlined" label={t('internal-service-management:settings_unsaved_changes')} />}
          <Button onClick={() => setDraft(undefined)} disabled={!isDirty || isSaving}>
            {t('internal-service-management:settings_reset_changes')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!isDirty || !isValid || isSaving}>
            {t('internal-service-management:settings_save')}
          </Button>
        </Stack>
        {performanceRuleKinds.map((kind, index) => (
          <Fragment key={kind}>
            {index > 0 && <Divider />}
            {renderRules(kind)}
          </Fragment>
        ))}
        <DiscardChangesDialog
          guard={guard}
          title={t('internal-service-management:settings_discard_title')}
          text={t('internal-service-management:settings_discard_confirm')}
          discardText={t('internal-service-management:settings_discard')}
        />
      </Stack>
    </QueryStateBoundary>
  )
}
